import { evaluateCustomerSpecialHandling } from '../../package-selection/customer-special-handling';
import {
  toFiniteNumber,
  type PackageSelectionFormValue,
} from '../../package-selection/forms/package-selection.form';
import {
  isCustomerPackaging,
  isExpressCataloguePackage,
  isExpressEnvelope,
  isExpressPackOrBox,
  type PackageTypeId,
} from '../../package-selection/package-types.config';
import {
  mapExpressToEstimateServices,
  type EstimateSpecialHandlingOptions,
} from '../../service-step/sections/delivery-service-selection/build-delivery-service-requests';

/** Short special-handling type keys shown on the Packaging review card (excluding Additional Handling). */
export type ReviewSpecialHandlingTypeKey =
  'largePackage' | 'residentialHeavyWeight' | 'oversized' | 'overMaximumLimit' | 'flatPackage';

/** Type keys that can appear as Special Handling rows, including Additional Handling. */
export type ReviewSpecialHandlingRowKey = ReviewSpecialHandlingTypeKey | 'additionalHandling';

/** One applicable handling type with its affected-piece count for Packaging summary. */
export interface ReviewSpecialHandlingRow {
  typeKey: ReviewSpecialHandlingRowKey;
  pieceCount: number;
}

/** Display model for the Packaging review card. */
export interface ReviewPackagingView {
  typeI18nKey: string;
  totalPieces: number;
  totalWeight: number;
  weightDisplay: string;
  weightUnit: 'lbs' | 'kg';
  specialHandlingRows: ReviewSpecialHandlingRow[];
}

const PACKAGE_TYPE_I18N_KEYS: Record<PackageTypeId, string> = {
  '1': 'createShipment.packageStep.types.expressEnvelope.label',
  '2': 'createShipment.packageStep.types.expressPack.label',
  '3': 'createShipment.packageStep.types.customerPackaging.label',
  '4': 'createShipment.packageStep.types.expressBox.label',
};

/**
 * OM-702 display order under Special Handling (OM-138 hierarchy slot for Additional Handling).
 */
const SPECIAL_HANDLING_ROW_DISPLAY_ORDER: ReviewSpecialHandlingRowKey[] = [
  'largePackage',
  'residentialHeavyWeight',
  'oversized',
  'overMaximumLimit',
  'additionalHandling',
  'flatPackage',
];

/** OM display order for special-handling type names (legacy key list without Additional Handling). */
const SPECIAL_HANDLING_DISPLAY_ORDER: ReviewSpecialHandlingTypeKey[] = [
  'largePackage',
  'residentialHeavyWeight',
  'oversized',
  'overMaximumLimit',
  'flatPackage',
];

/** Shipment Lambda SH codes (excluding SH06 additional handling) to Packaging review keys. */
const SH_CODE_TO_REVIEW_KEY: Record<string, ReviewSpecialHandlingTypeKey> = {
  SH05: 'flatPackage',
  SH07: 'largePackage',
  SH08: 'overMaximumLimit',
  SH09: 'oversized',
  SH10: 'residentialHeavyWeight',
};

/** SH codes including Additional Handling for SUCCESS packaging rows. */
const SH_CODE_TO_ROW_KEY: Record<string, ReviewSpecialHandlingRowKey> = {
  ...SH_CODE_TO_REVIEW_KEY,
  SH06: 'additionalHandling',
};

/**
 * Maps Shipment Lambda special-handling type codes onto Packaging review-card keys.
 * Unknown codes and SH06 (additional handling) are ignored.
 *
 * @param types Rated special-handling types from SUCCESS packaging
 */
export function mapSpecialHandlingTypeCodes(
  types: { code: string }[],
): ReviewSpecialHandlingTypeKey[] {
  const keys = new Set<ReviewSpecialHandlingTypeKey>();
  for (const type of types) {
    const key = SH_CODE_TO_REVIEW_KEY[type.code.trim().toUpperCase()];
    if (key) {
      keys.add(key);
    }
  }

  return SPECIAL_HANDLING_DISPLAY_ORDER.filter((key) => keys.has(key));
}

/**
 * Builds Special Handling rows from SUCCESS packaging type codes and Additional Handling count.
 * Non-SH06 types without an explicit count default to 1 piece each (distinct type presence).
 *
 * @param types Rated special-handling types from SUCCESS packaging
 * @param additionalHandlingCount Hierarchy-aware SH06 piece count from SUCCESS
 */
export function mapSpecialHandlingRowsFromCodes(
  types: { code: string }[],
  additionalHandlingCount = 0,
): ReviewSpecialHandlingRow[] {
  const counts = new Map<ReviewSpecialHandlingRowKey, number>();

  for (const type of types) {
    const key = SH_CODE_TO_ROW_KEY[type.code.trim().toUpperCase()];
    if (!key || key === 'additionalHandling') {
      continue;
    }
    counts.set(key, (counts.get(key) ?? 0) + 1);
  }

  if (additionalHandlingCount > 0) {
    counts.set('additionalHandling', additionalHandlingCount);
  }

  return SPECIAL_HANDLING_ROW_DISPLAY_ORDER.filter((key) => (counts.get(key) ?? 0) > 0).map(
    (typeKey) => ({ typeKey, pieceCount: counts.get(typeKey)! }),
  );
}

/**
 * Builds Packaging Special Handling rows from backend-rated piece counts per SH code (OM-884).
 * Uses hierarchy-aware counts from Estimate Manager `ratingInput.items` so Residential Area
 * Heavyweight is not mislabeled as Oversized when the wizard lacks destination LDD.
 *
 * @param pieceCounts Piece totals keyed by upper-case service code (e.g. SH09, SH10)
 */
export function mapSpecialHandlingRowsFromPieceCounts(
  pieceCounts: Record<string, number>,
): ReviewSpecialHandlingRow[] {
  const counts = new Map<ReviewSpecialHandlingRowKey, number>();

  for (const [rawCode, pieceCount] of Object.entries(pieceCounts)) {
    if (!Number.isFinite(pieceCount) || pieceCount <= 0) {
      continue;
    }
    const key = SH_CODE_TO_ROW_KEY[rawCode.trim().toUpperCase()];
    if (!key) {
      continue;
    }
    counts.set(key, (counts.get(key) ?? 0) + pieceCount);
  }

  return SPECIAL_HANDLING_ROW_DISPLAY_ORDER.filter((key) => (counts.get(key) ?? 0) > 0).map(
    (typeKey) => ({ typeKey, pieceCount: counts.get(typeKey)! }),
  );
}

/**
 * Resolves the i18n key for a persisted package type id.
 */
export function packageTypeI18nKey(packageTypeId: PackageTypeId | null): string {
  if (!packageTypeId) {
    return PACKAGE_TYPE_I18N_KEYS['3'];
  }

  return PACKAGE_TYPE_I18N_KEYS[packageTypeId] ?? PACKAGE_TYPE_I18N_KEYS['3'];
}

/**
 * Formats a shipment weight with up to two decimal places (trailing zeros dropped).
 */
export function formatReviewWeight(totalWeight: number): string {
  if (!Number.isFinite(totalWeight)) {
    return '0';
  }

  const rounded = Math.round(totalWeight * 100) / 100;
  return String(rounded);
}

/**
 * Sums customer-packaging piece counts (identicalPieces).
 * Coerces text-input strings the same way as the Package step (`toFiniteNumber`).
 */
function totalCustomerPieces(packageValue: PackageSelectionFormValue): number {
  return packageValue.pieces.reduce((sum, piece) => {
    const count = toFiniteNumber(piece.identicalPieces);
    return sum + Math.max(0, count);
  }, 0);
}

/**
 * Sums customer-packaging weight as pieces × per-piece weight.
 * Coerces text-input strings (`puro-input type="text"` often persists strings).
 */
function totalCustomerWeight(packageValue: PackageSelectionFormValue): number {
  return packageValue.pieces.reduce((sum, piece) => {
    const count = Math.max(0, toFiniteNumber(piece.identicalPieces));
    const weight = Math.max(0, toFiniteNumber(piece.perPieceWeight));
    return sum + count * weight;
  }, 0);
}

/**
 * Aggregates hierarchy winners (and over-max) into Special Handling rows with piece counts.
 * Additional Handling is included only when it remains the applicable type after OM-138 hierarchy.
 */
function specialHandlingRows(
  packageValue: PackageSelectionFormValue,
  options?: EstimateSpecialHandlingOptions,
): ReviewSpecialHandlingRow[] {
  const counts = new Map<ReviewSpecialHandlingRowKey, number>();

  if (isExpressCataloguePackage(packageValue.packageTypeId)) {
    for (const service of mapExpressToEstimateServices(packageValue, options)) {
      const key = SH_CODE_TO_ROW_KEY[(service.serviceCode ?? '').trim().toUpperCase()];
      if (key) {
        counts.set(key, (counts.get(key) ?? 0) + 1);
      }
    }
    return SPECIAL_HANDLING_ROW_DISPLAY_ORDER.filter((key) => (counts.get(key) ?? 0) > 0).map(
      (typeKey) => ({ typeKey, pieceCount: counts.get(typeKey)! }),
    );
  }

  const shContext = {
    destinationResidential:
      options?.destinationResidential === true && options?.destinationLowDensity === true,
  };

  for (const piece of packageValue.pieces) {
    const pieceCount = Math.max(1, toFiniteNumber(piece.identicalPieces));
    for (const type of evaluateCustomerSpecialHandling(piece, packageValue.unitSystem, shContext)) {
      counts.set(type, (counts.get(type) ?? 0) + pieceCount);
    }
  }

  return SPECIAL_HANDLING_ROW_DISPLAY_ORDER.filter((key) => (counts.get(key) ?? 0) > 0).map(
    (typeKey) => ({ typeKey, pieceCount: counts.get(typeKey)! }),
  );
}

/**
 * Maps persisted Package-step state into Packaging review-card fields.
 */
export function mapReviewPackaging(
  packageValue: PackageSelectionFormValue | undefined,
  options?: EstimateSpecialHandlingOptions,
): ReviewPackagingView {
  if (!packageValue) {
    return {
      typeI18nKey: packageTypeI18nKey(null),
      totalPieces: 0,
      totalWeight: 0,
      weightDisplay: '0',
      weightUnit: 'lbs',
      specialHandlingRows: [],
    };
  }

  const express =
    isExpressEnvelope(packageValue.packageTypeId) || isExpressPackOrBox(packageValue.packageTypeId);
  const totalPieces = express ? 1 : totalCustomerPieces(packageValue);
  const totalWeight = isCustomerPackaging(packageValue.packageTypeId)
    ? totalCustomerWeight(packageValue)
    : toFiniteNumber(packageValue.expressTotalWeight);

  return {
    typeI18nKey: packageTypeI18nKey(packageValue.packageTypeId),
    totalPieces,
    totalWeight,
    weightDisplay: formatReviewWeight(totalWeight),
    weightUnit: packageValue.unitSystem === 'metric' ? 'kg' : 'lbs',
    specialHandlingRows: specialHandlingRows(packageValue, options),
  };
}
